import { BIOME_IDS } from '../biomes.js';
import { footprintOf } from '../props.js';
import { terrainOf } from '../terrains.js';
import { isCliffCell } from '../../world/relief.js';
/**
 * The world's heights, one by one (ADR 0020, 0021): every hill terrace and
 * summit, whether a player can climb to it, and what stands on it. A census
 * for the reports and the previews, never read by the game: a height with
 * nothing on it is a climb that pays nothing.
 */
/** A smaller patch is a ledge, not a place. */
export const MIN_PATCH = 40;
/**
 * What grows by itself, as `world-props.ts` scatters it: trees, reeds,
 * mushrooms, flowers, bushes, boulders and the snow's ice crystals, stumps,
 * cacti. Their presence does not make a height worth the climb.
 */
export const NATURE_PROPS = new Set([
    'oak',
    'pine',
    'snowy-pine',
    'dead-tree',
    'palm',
    'bush',
    'dry-bush',
    'boulder',
    'ice-crystal',
    'stump',
    'flowers',
    'grass',
    'mushrooms',
    'reeds',
    'cactus',
]);
/** The ways up: they lead to a height, they are not what is there. */
const STAIRS = new Set(['stairs-grass', 'stairs-sand', 'stairs-snow', 'stairs-marsh']);
const HATCH = 'summit-hole';
/**
 * Each connected patch (orthogonal, one level) of flat tiles at height 1 or
 * more, at least `MIN_PATCH` tiles, largest first. A flat tile is one whose
 * corners all stand at its own height (no cliff edge nor face) and that is
 * not mountain rock: a mesa's top counts, though nobody walks it, so the
 * census shows it as out of reach rather than leaving it out.
 *
 * `reached`, if given, says which tiles a player walks to instead of `rows`:
 * the generator asks before its rows exist (ADR 0092).
 */
export function listHeights(world, reached) {
    const { width, height } = world;
    const heights = world.heights ?? [];
    const levelAt = (x, y) => Number(heights[Math.min(height - 1, Math.max(0, y))]?.[Math.min(width - 1, Math.max(0, x))] ?? 0);
    const inside = (x, y) => x >= 0 && y >= 0 && x < width && y < height;
    const flat = (x, y) => {
        if (levelAt(x, y) < 1 || isCliffCell(levelAt, x, y))
            return false;
        const char = world.ground?.[y]?.[x];
        return char === undefined || terrainOf(char).id !== 'rock';
    };
    const patchOf = new Int32Array(width * height).fill(-1);
    const patches = [];
    for (let start = 0; start < width * height; start += 1) {
        const sx = start % width;
        const sy = (start - sx) / width;
        if (patchOf[start] !== -1 || !flat(sx, sy))
            continue;
        const level = levelAt(sx, sy);
        const id = patches.length;
        const tiles = [start];
        patchOf[start] = id;
        // The array grows as the flood goes: for-of walks what it pushes too.
        for (const i of tiles) {
            const x = i % width;
            const y = (i - x) / width;
            for (const [nx, ny] of [
                [x + 1, y],
                [x - 1, y],
                [x, y + 1],
                [x, y - 1],
            ]) {
                if (!inside(nx, ny))
                    continue;
                const j = ny * width + nx;
                if (patchOf[j] !== -1 || levelAt(nx, ny) !== level || !flat(nx, ny))
                    continue;
                patchOf[j] = id;
                tiles.push(j);
            }
        }
        patches.push({ level, tiles });
    }
    // The generator knows better than `rows` what it walks to (caves included).
    const walked = reached ?? walkFrom(world);
    const tally = patches.map(() => ({
        stairs: 0,
        hatches: 0,
        nature: 0,
        content: [],
        places: [],
    }));
    const at = (x, y) => inside(x, y) ? tally[patchOf[y * width + x] ?? -1] : undefined;
    for (const placed of world.props) {
        const box = footprintOf(placed);
        if (STAIRS.has(placed.prop)) {
            // The flight climbs its face from the tile under it to the one above.
            for (const y of [box.y0 - 1, box.y1 + 1]) {
                const end = at(box.x0, y);
                if (end !== undefined)
                    end.stairs += 1;
            }
            continue;
        }
        // A prop belongs to the first patch its footprint stands on.
        let on;
        for (let y = box.y0; y <= box.y1 && on === undefined; y += 1) {
            for (let x = box.x0; x <= box.x1 && on === undefined; x += 1)
                on = at(x, y);
        }
        if (on === undefined)
            continue;
        if (placed.prop === HATCH)
            on.hatches += 1;
        else if (NATURE_PROPS.has(placed.prop))
            on.nature += 1;
        else
            on.content.push(placed.prop);
    }
    for (const spawn of world.spawns) {
        at(spawn.x, spawn.y)?.content.push(`den:${spawn.mob}${spawn.camp === undefined ? '' : '(camp)'}`);
    }
    for (const chest of world.chests)
        at(chest.x, chest.y)?.content.push('chest');
    for (const landmark of world.landmarks) {
        at(landmark.x, landmark.y)?.places.push(landmark.name);
    }
    return patches
        .flatMap((patch, id) => {
        const count = tally[id];
        return count === undefined || patch.tiles.length < MIN_PATCH ? [] : [{ patch, count }];
    })
        .map(({ patch, count }) => {
        let sumX = 0;
        let sumY = 0;
        const biomes = new Array(BIOME_IDS.length).fill(0);
        for (const i of patch.tiles) {
            const x = i % width;
            const y = (i - x) / width;
            sumX += x;
            sumY += y;
            const b = Number(world.biomes?.[y]?.[x] ?? 0);
            biomes[b] = (biomes[b] ?? 0) + 1;
        }
        const biome = BIOME_IDS[biomes.indexOf(Math.max(...biomes))] ?? 'meadow';
        return {
            level: patch.level,
            biome,
            area: patch.tiles.length,
            centre: {
                x: Math.round(sumX / patch.tiles.length),
                y: Math.round(sumY / patch.tiles.length),
            },
            stairs: count.stairs,
            hatches: count.hatches,
            reachable: count.hatches > 0 || patch.tiles.some((i) => walked[i] === 1),
            nature: count.nature,
            content: count.content,
            places: count.places,
            tiles: patch.tiles,
        };
    })
        .sort((a, b) => b.area - a.area || a.centre.y - b.centre.y || a.centre.x - b.centre.x);
}
/** Every tile open in `rows` that one walks to from the spawn, orthogonally. */
function walkFrom(world) {
    const { width, height } = world;
    const open = (x, y) => x >= 0 && y >= 0 && x < width && y < height && world.rows[y]?.[x] !== '#';
    const walked = new Uint8Array(width * height);
    if (!open(world.spawn.x, world.spawn.y))
        return walked;
    const stack = [world.spawn.y * width + world.spawn.x];
    walked[stack[0] ?? 0] = 1;
    while (stack.length > 0) {
        const i = stack.pop() ?? 0;
        const x = i % width;
        const y = (i - x) / width;
        for (const [nx, ny] of [
            [x + 1, y],
            [x - 1, y],
            [x, y + 1],
            [x, y - 1],
        ]) {
            const j = ny * width + nx;
            if (!open(nx, ny) || walked[j] === 1)
                continue;
            walked[j] = 1;
            stack.push(j);
        }
    }
    return walked;
}
//# sourceMappingURL=world-heights.js.map